import React, { useState, useEffect, useMemo, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { toast } from 'react-hot-toast';
import {
  HiMagnifyingGlass,
  HiFunnel,
  HiArrowDownTray,
  HiPencilSquare,
  HiTrash,
  HiEye,
  HiPlus,
  HiXMark,
  HiCheckCircle,
  HiClock,
  HiXCircle,
  HiArrowUpDown,
} from 'react-icons/hi2';
import { useAuth } from '../../context/AuthContext';
import { useUserRole } from '../../context/UserRoleContext';
import {
  getAllSubmissions,
  canViewAll,
  deleteReadinessChecklist,
  deleteGTMPlan,
  deleteLaunchChecklist,
} from '../../services/commercialisationService';
import { firestore } from '../../database/firebase';
import {
  collection,
  doc,
  getDoc,
  getDocs,
  query,
  where,
  orderBy,
  limit,
  startAt,
  endAt,
  onSnapshot,
} from 'firebase/firestore';
import CommercialisationReadinessChecklist from './CommercialisationReadinessChecklist';
import GTMStrategyPlan from './GTMStrategyPlan';
import StartupLaunchChecklist from './StartupLaunchChecklist';

const AdminPanel = () => {
  const { currentUser } = useAuth();
  const { userRole } = useUserRole();
  const [submissions, setSubmissions] = useState([]);
  const [filteredSubmissions, setFilteredSubmissions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [sortBy, setSortBy] = useState('updatedAt');
  const [sortOrder, setSortOrder] = useState('desc');
  const [selectedSubmission, setSelectedSubmission] = useState(null);
  const [viewMode, setViewMode] = useState('table'); // 'table' or 'details'
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [exporting, setExporting] = useState(false);
  const [showExportMenu, setShowExportMenu] = useState(false);
  const exportMenuRef = useRef(null);

  // Check if user has access
  const hasAccess = canViewAll(userRole);

  // Close export menu when clicking outside
  useEffect(() => {
    const handleClickOutside = event => {
      if (exportMenuRef.current && !exportMenuRef.current.contains(event.target)) {
        setShowExportMenu(false);
      }
    };

    if (showExportMenu) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showExportMenu]);

  // Cache client names map - only reload when needed
  const [clientsMap, setClientsMap] = useState({});
  const [clientsMapLoaded, setClientsMapLoaded] = useState(false);

  // Load client names once and cache
  useEffect(() => {
    if (!hasAccess || clientsMapLoaded) return;

    const loadClientNames = async () => {
      try {
        const clientsSnapshot = await getDocs(collection(firestore, 'clients'));
        const map = {};
        clientsSnapshot.forEach(doc => {
          map[doc.id] = {
            name:
              `${doc.data().firstname || ''} ${doc.data().lastname || ''}`.trim() ||
              doc.data().email ||
              'Unknown',
            email: doc.data().email || '',
          };
        });
        setClientsMap(map);
        setClientsMapLoaded(true);
      } catch (error) {
        console.error('Failed to load client names:', error);
        setClientsMapLoaded(true); // Set to true to prevent retry loops
      }
    };

    loadClientNames();
  }, [hasAccess, clientsMapLoaded]);

  useEffect(() => {
    if (!hasAccess) {
      setLoading(false);
      return;
    }

    if (!clientsMapLoaded) {
      // Wait for client names to load first
      return;
    }

    // Real-time listener for commercialisation collection
    const commercialisationRef = collection(firestore, 'commercialisation');

    const unsubscribe = onSnapshot(
      commercialisationRef,
      async snapshot => {
        try {
          setLoading(true);
          const submissions = [];

          // Process all user documents in parallel
          const userPromises = snapshot.docs.map(async userDoc => {
            const userId = userDoc.id;
            const userData = { userId, ...userDoc.data() };

            // Fetch all three checklists in parallel
            const [readinessSnap, gtmSnap, launchSnap] = await Promise.all([
              getDoc(doc(firestore, 'commercialisation', userId, 'readinessChecklist', userId)),
              getDoc(doc(firestore, 'commercialisation', userId, 'gtmPlan', userId)),
              getDoc(doc(firestore, 'commercialisation', userId, 'launchChecklist', userId)),
            ]);

            if (readinessSnap.exists()) {
              userData.readinessChecklist = { id: readinessSnap.id, ...readinessSnap.data() };
            }
            if (gtmSnap.exists()) {
              userData.gtmPlan = { id: gtmSnap.id, ...gtmSnap.data() };
            }
            if (launchSnap.exists()) {
              userData.launchChecklist = { id: launchSnap.id, ...launchSnap.data() };
            }

            // Enrich with client names
            return {
              ...userData,
              clientName: clientsMap[userId]?.name || userData.userEmail || 'Unknown',
              clientEmail: clientsMap[userId]?.email || userData.userEmail || '',
            };
          });

          const results = await Promise.all(userPromises);
          setSubmissions(results);
          setFilteredSubmissions(results);
        } catch (error) {
          console.error('Failed to load submissions:', error);
          toast.error('Failed to load submissions');
        } finally {
          setLoading(false);
        }
      },
      error => {
        console.error('Snapshot error:', error);
        toast.error('Failed to load submissions');
        setLoading(false);
      }
    );

    return () => unsubscribe();
  }, [currentUser, hasAccess, clientsMap, clientsMapLoaded]);

  // Filter and sort submissions
  useEffect(() => {
    let filtered = [...submissions];

    // Search filter
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter(sub => {
        const name = (sub.clientName || '').toLowerCase();
        const email = (sub.clientEmail || '').toLowerCase();
        const status = (sub.readinessChecklist?.readinessLevel || '').toLowerCase();
        return name.includes(term) || email.includes(term) || status.includes(term);
      });
    }

    // Status filter
    if (statusFilter !== 'all') {
      filtered = filtered.filter(sub => {
        const status = sub.readinessChecklist?.readinessLevel || 'Not Ready';
        if (statusFilter === 'ready') return status.includes('Ready to Commercialise');
        if (statusFilter === 'almost') return status.includes('Almost Ready');
        if (statusFilter === 'not') return status.includes('Not Ready');
        return true;
      });
    }

    // Sort
    filtered.sort((a, b) => {
      let aValue, bValue;

      if (sortBy === 'name') {
        aValue = a.clientName || '';
        bValue = b.clientName || '';
      } else if (sortBy === 'score') {
        aValue = a.readinessChecklist?.score || 0;
        bValue = b.readinessChecklist?.score || 0;
      } else if (sortBy === 'updatedAt') {
        aValue = a.readinessChecklist?.updatedAt?.toMillis?.() || 0;
        bValue = b.readinessChecklist?.updatedAt?.toMillis?.() || 0;
      } else {
        aValue = a[sortBy] || 0;
        bValue = b[sortBy] || 0;
      }

      if (sortOrder === 'asc') {
        return aValue > bValue ? 1 : -1;
      } else {
        return aValue < bValue ? 1 : -1;
      }
    });

    setFilteredSubmissions(filtered);
  }, [submissions, searchTerm, statusFilter, sortBy, sortOrder]);

  const handleDelete = async () => {
    if (!deleteTarget) return;

    setLoading(true);
    try {
      const { userId, type } = deleteTarget;

      if (type === 'readiness') {
        await deleteReadinessChecklist(userId, currentUser);
        // Remove from local state
        setSubmissions(prev =>
          prev.map(s => {
            if (s.userId === userId) {
              const updated = { ...s };
              delete updated.readinessChecklist;
              return updated;
            }
            return s;
          })
        );
      } else if (type === 'gtm') {
        await deleteGTMPlan(userId, currentUser);
        // Remove from local state
        setSubmissions(prev =>
          prev.map(s => {
            if (s.userId === userId) {
              const updated = { ...s };
              delete updated.gtmPlan;
              return updated;
            }
            return s;
          })
        );
      } else if (type === 'launch') {
        await deleteLaunchChecklist(userId, currentUser);
        // Remove from local state
        setSubmissions(prev =>
          prev.map(s => {
            if (s.userId === userId) {
              const updated = { ...s };
              delete updated.launchChecklist;
              return updated;
            }
            return s;
          })
        );
      }

      setShowDeleteModal(false);
      setDeleteTarget(null);
      toast.success('Record deleted successfully');
    } catch (error) {
      toast.error(error.message || 'Failed to delete record');
    } finally {
      setLoading(false);
    }
  };

  const handleExport = async format => {
    setExporting(true);
    setShowExportMenu(false);
    try {
      const data = filteredSubmissions.map(sub => ({
        'Client Name': sub.clientName || '',
        'Client Email': sub.clientEmail || '',
        'Readiness Score': sub.readinessChecklist?.score || 0,
        'Readiness Status': sub.readinessChecklist?.readinessLevel || 'Not Ready',
        'GTM Progress': sub.gtmPlan?.progress || 0,
        'Launch Score': sub.launchChecklist?.score || 0,
        'Launch Status': sub.launchChecklist?.readinessLevel || 'Not Ready',
        'Last Updated':
          sub.readinessChecklist?.updatedAt?.toDate?.()?.toLocaleString() ||
          sub.readinessChecklist?.updatedAt ||
          '',
        'Created At':
          sub.readinessChecklist?.createdAt?.toDate?.()?.toLocaleString() ||
          sub.readinessChecklist?.createdAt ||
          '',
      }));

      if (format === 'csv') {
        exportToCSV(data);
      } else if (format === 'xlsx') {
        await exportToXLSX(data);
      } else if (format === 'pdf') {
        await exportToPDF(data);
      }
    } catch (error) {
      toast.error('Failed to export data');
    } finally {
      setExporting(false);
    }
  };

  const exportToCSV = data => {
    if (!data || data.length === 0) {
      toast.error('No data to export');
      return;
    }

    const headers = Object.keys(data[0] || {});
    const csvContent = [
      headers.join(','),
      ...data.map(row =>
        headers
          .map(header => {
            const value = String(row[header] || '').replace(/"/g, '""');
            return `"${value}"`;
          })
          .join(',')
      ),
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `commercialisation_export_${new Date().toISOString().split('T')[0]}.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(link.href);
    toast.success('CSV exported successfully');
  };

  const exportToXLSX = async data => {
    try {
      // Try to use xlsx library if available
      try {
        const XLSX = await import('xlsx');
        const ws = XLSX.utils.json_to_sheet(data);
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, 'Commercialisation Data');
        XLSX.writeFile(
          wb,
          `commercialisation_export_${new Date().toISOString().split('T')[0]}.xlsx`
        );
        toast.success('Excel file exported successfully');
      } catch (importError) {
        exportToCSV(data);
        toast.success('Exported as CSV (Excel library not available)');
      }
    } catch (error) {
      toast.error('Failed to export Excel file');
    }
  };

  const exportToPDF = async data => {
    try {
      // Try to use jspdf library if available
      try {
        const jsPDF = (await import('jspdf')).default;
        const doc = new jsPDF();

        doc.setFontSize(16);
        doc.text('Commercialisation Data Export', 14, 20);
        doc.setFontSize(10);
        doc.text(`Exported: ${new Date().toLocaleString()}`, 14, 30);
        doc.text(`Total Records: ${data.length}`, 14, 35);
        doc.text(`Exported By: ${currentUser?.email || 'Unknown'}`, 14, 40);

        let y = 50;
        const pageHeight = doc.internal.pageSize.height;
        const headers = Object.keys(data[0] || {});

        doc.setFontSize(8);
        headers.forEach((header, i) => {
          doc.text(header.substring(0, 15), 14 + i * 35, y);
        });
        y += 10;

        data.forEach((row, index) => {
          if (y > pageHeight - 20) {
            doc.addPage();
            y = 20;
          }
          headers.forEach((header, i) => {
            const value = String(row[header] || '').substring(0, 15);
            doc.text(value, 14 + i * 35, y);
          });
          y += 7;
        });

        doc.save(`commercialisation_export_${new Date().toISOString().split('T')[0]}.pdf`);
        toast.success('PDF exported successfully');
      } catch (importError) {
        exportToCSV(data);
        toast.success('Exported as CSV (PDF library not available)');
      }
    } catch (error) {
      toast.error('Failed to export PDF');
    }
  };

  const getStatusBadge = status => {
    if (!status) return null;

    if (status.includes('Ready to Commercialise')) {
      return (
        <span className="inline-flex items-center gap-1 rounded-full bg-green-100 px-3 py-1 text-xs font-medium text-green-800">
          <HiCheckCircle className="h-3 w-3" />
          Ready
        </span>
      );
    } else if (status.includes('Almost Ready')) {
      return (
        <span className="inline-flex items-center gap-1 rounded-full bg-yellow-100 px-3 py-1 text-xs font-medium text-yellow-800">
          <HiClock className="h-3 w-3" />
          Almost Ready
        </span>
      );
    } else {
      return (
        <span className="inline-flex items-center gap-1 rounded-full bg-red-100 px-3 py-1 text-xs font-medium text-red-800">
          <HiXCircle className="h-3 w-3" />
          Not Ready
        </span>
      );
    }
  };

  if (!hasAccess) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-white">
        <div className="text-center">
          <p className="text-lg font-medium text-gray-900">Access Denied</p>
          <p className="mt-2 text-sm text-gray-600">
            You need super admin, admin, moderator, coach, or business advisor privileges to access
            this panel.
          </p>
        </div>
      </div>
    );
  }

  if (loading && submissions.length === 0) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-white">
        <div className="text-center">
          <div className="mx-auto mb-4 h-8 w-8 animate-spin rounded-full border-2 border-gray-300 border-t-blue-600" />
          <p className="text-sm text-gray-500">Loading submissions...</p>
        </div>
      </div>
    );
  }

  if (viewMode === 'details' && selectedSubmission) {
    return (
      <div className="min-h-screen bg-white">
        <div className="mx-auto max-w-7xl px-4 py-6 sm:px-6 md:py-8 lg:px-8">
          <div className="mb-6 flex items-center justify-between">
            <div>
              <h2 className="text-2xl font-bold text-black">
                {selectedSubmission.type === 'readiness' && 'Commercialisation Readiness Checklist'}
                {selectedSubmission.type === 'gtm' && 'Go-to-Market Strategy Plan'}
                {selectedSubmission.type === 'launch' && 'Start-Up Launch Checklist'}
              </h2>
              <p className="mt-1 text-sm text-gray-600">
                Client:{' '}
                {submissions.find(s => s.userId === selectedSubmission.userId)?.clientName ||
                  'Unknown'}
              </p>
            </div>
            <button
              onClick={() => {
                setViewMode('table');
                setSelectedSubmission(null);
              }}
              className="inline-flex items-center gap-2 rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 shadow-sm transition-colors hover:bg-gray-50"
            >
              <HiXMark className="h-4 w-4" />
              Back to List
            </button>
          </div>
          {selectedSubmission.type === 'readiness' && (
            <CommercialisationReadinessChecklist
              userId={selectedSubmission.userId}
              userRole={userRole}
            />
          )}
          {selectedSubmission.type === 'gtm' && (
            <GTMStrategyPlan userId={selectedSubmission.userId} userRole={userRole} />
          )}
          {selectedSubmission.type === 'launch' && (
            <StartupLaunchChecklist userId={selectedSubmission.userId} userRole={userRole} />
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="mx-auto max-w-7xl px-4 py-6 sm:px-6 md:py-8 lg:px-8">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="mb-2 text-3xl font-bold text-black">Commercialisation Admin Panel</h1>
              <p className="text-gray-600">
                Manage and view all client commercialisation submissions
              </p>
            </div>
          </div>
        </div>

        {/* Toolbar */}
        <div className="mb-6 rounded-xl border border-gray-200 bg-white p-4 shadow-sm">
          <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
            {/* Search */}
            <div className="relative flex-1">
              <HiMagnifyingGlass className="absolute left-3 top-1/2 h-5 w-5 -translate-y-1/2 text-gray-400" />
              <input
                type="text"
                value={searchTerm}
                onChange={e => setSearchTerm(e.target.value)}
                placeholder="Search by client name, email, or status..."
                className="w-full rounded-lg border border-gray-300 bg-white py-2 pl-10 pr-4 text-sm focus:border-blue-500 focus:ring-2 focus:ring-blue-500/10"
              />
            </div>

            {/* Filters */}
            <div className="flex items-center gap-3">
              <select
                value={statusFilter}
                onChange={e => setStatusFilter(e.target.value)}
                className="rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm focus:border-blue-500 focus:ring-2 focus:ring-blue-500/10"
              >
                <option value="all">All Statuses</option>
                <option value="ready">Ready to Commercialise</option>
                <option value="almost">Almost Ready</option>
                <option value="not">Not Ready</option>
              </select>

              <select
                value={`${sortBy}-${sortOrder}`}
                onChange={e => {
                  const [field, order] = e.target.value.split('-');
                  setSortBy(field);
                  setSortOrder(order);
                }}
                className="rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm focus:border-blue-500 focus:ring-2 focus:ring-blue-500/10"
              >
                <option value="name-asc">Name (A-Z)</option>
                <option value="name-desc">Name (Z-A)</option>
                <option value="score-desc">Score (High-Low)</option>
                <option value="score-asc">Score (Low-High)</option>
                <option value="updatedAt-desc">Last Updated (Newest)</option>
                <option value="updatedAt-asc">Last Updated (Oldest)</option>
              </select>

              {/* Export Dropdown */}
              <div className="relative" ref={exportMenuRef}>
                <button
                  onClick={() => setShowExportMenu(!showExportMenu)}
                  disabled={exporting}
                  className="inline-flex items-center gap-2 rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 shadow-sm transition-colors hover:bg-gray-50 disabled:opacity-50"
                >
                  <HiArrowDownTray className="h-4 w-4" />
                  {exporting ? 'Exporting...' : 'Export'}
                </button>
                {showExportMenu && !exporting && (
                  <div className="absolute right-0 top-full z-10 mt-2 w-48 rounded-lg border border-gray-200 bg-white shadow-lg">
                    <button
                      onClick={() => handleExport('csv')}
                      className="w-full px-4 py-2 text-left text-sm transition-colors hover:bg-gray-50"
                    >
                      Export as CSV
                    </button>
                    <button
                      onClick={() => handleExport('xlsx')}
                      className="w-full px-4 py-2 text-left text-sm transition-colors hover:bg-gray-50"
                    >
                      Export as Excel
                    </button>
                    <button
                      onClick={() => handleExport('pdf')}
                      className="w-full px-4 py-2 text-left text-sm transition-colors hover:bg-gray-50"
                    >
                      Export as PDF
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Stats */}
        <div className="mb-6 grid grid-cols-1 gap-4 md:grid-cols-4">
          <div className="rounded-lg border border-gray-200 bg-white p-4">
            <div className="text-sm font-medium text-gray-600">Total Clients</div>
            <div className="mt-2 text-2xl font-bold text-gray-900">
              {filteredSubmissions.length}
            </div>
          </div>
          <div className="rounded-lg border border-gray-200 bg-white p-4">
            <div className="text-sm font-medium text-gray-600">Ready</div>
            <div className="mt-2 text-2xl font-bold text-green-600">
              {
                filteredSubmissions.filter(s =>
                  s.readinessChecklist?.readinessLevel?.includes('Ready to Commercialise')
                ).length
              }
            </div>
          </div>
          <div className="rounded-lg border border-gray-200 bg-white p-4">
            <div className="text-sm font-medium text-gray-600">Almost Ready</div>
            <div className="mt-2 text-2xl font-bold text-yellow-600">
              {
                filteredSubmissions.filter(s =>
                  s.readinessChecklist?.readinessLevel?.includes('Almost Ready')
                ).length
              }
            </div>
          </div>
          <div className="rounded-lg border border-gray-200 bg-white p-4">
            <div className="text-sm font-medium text-gray-600">Not Ready</div>
            <div className="mt-2 text-2xl font-bold text-red-600">
              {
                filteredSubmissions.filter(s =>
                  s.readinessChecklist?.readinessLevel?.includes('Not Ready')
                ).length
              }
            </div>
          </div>
        </div>

        {/* Data Table */}
        <div className="overflow-hidden rounded-xl border border-gray-200 bg-white shadow-sm">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                    Client
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                    Readiness
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                    GTM Progress
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                    Launch
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                    Last Updated
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium uppercase tracking-wider text-gray-500">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200 bg-white">
                {filteredSubmissions.length === 0 ? (
                  <tr>
                    <td colSpan={6} className="px-6 py-12 text-center text-sm text-gray-500">
                      No submissions found
                    </td>
                  </tr>
                ) : (
                  filteredSubmissions.map(submission => (
                    <tr key={submission.userId} className="hover:bg-gray-50">
                      <td className="whitespace-nowrap px-6 py-4">
                        <div className="text-sm font-medium text-gray-900">
                          {submission.clientName || 'Unknown'}
                        </div>
                        <div className="text-sm text-gray-500">{submission.clientEmail || ''}</div>
                      </td>
                      <td className="whitespace-nowrap px-6 py-4">
                        <div className="flex items-center gap-2">
                          <div className="text-sm font-medium text-gray-900">
                            {submission.readinessChecklist?.score || 0}%
                          </div>
                          {getStatusBadge(submission.readinessChecklist?.readinessLevel)}
                        </div>
                      </td>
                      <td className="whitespace-nowrap px-6 py-4">
                        <div className="text-sm text-gray-900">
                          {submission.gtmPlan?.progress || 0}%
                        </div>
                      </td>
                      <td className="whitespace-nowrap px-6 py-4">
                        <div className="flex items-center gap-2">
                          <div className="text-sm font-medium text-gray-900">
                            {submission.launchChecklist?.score || 0}%
                          </div>
                          {getStatusBadge(submission.launchChecklist?.readinessLevel)}
                        </div>
                      </td>
                      <td className="whitespace-nowrap px-6 py-4 text-sm text-gray-500">
                        {submission.readinessChecklist?.updatedAt
                          ?.toDate?.()
                          ?.toLocaleDateString() || 'N/A'}
                      </td>
                      <td className="whitespace-nowrap px-6 py-4 text-right text-sm font-medium">
                        <div className="flex items-center justify-end gap-1">
                          <button
                            onClick={() => {
                              setSelectedSubmission({
                                userId: submission.userId,
                                type: 'readiness',
                              });
                              setViewMode('details');
                            }}
                            className="rounded-lg p-2 text-blue-600 transition-colors hover:bg-blue-50"
                            title="View Readiness Checklist"
                          >
                            <HiEye className="h-4 w-4" />
                          </button>
                          {submission.readinessChecklist && (
                            <button
                              onClick={() => {
                                setDeleteTarget({ userId: submission.userId, type: 'readiness' });
                                setShowDeleteModal(true);
                              }}
                              className="rounded-lg p-2 text-red-600 transition-colors hover:bg-red-50"
                              title="Delete Readiness Checklist"
                            >
                              <HiTrash className="h-4 w-4" />
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* Delete Confirmation Modal */}
      <AnimatePresence>
        {showDeleteModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4 backdrop-blur-sm"
            onClick={() => setShowDeleteModal(false)}
          >
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.95 }}
              onClick={e => e.stopPropagation()}
              className="w-full max-w-md rounded-xl border border-gray-200 bg-white p-6 shadow-xl"
            >
              <div className="mb-4 flex items-center gap-3">
                <div className="flex h-10 w-10 items-center justify-center rounded-full bg-red-100">
                  <HiTrash className="h-5 w-5 text-red-600" />
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-gray-900">Delete Record</h3>
                  <p className="text-sm text-gray-600">This action cannot be undone</p>
                </div>
              </div>
              <p className="mb-6 text-sm text-gray-700">
                Are you sure you want to delete this{' '}
                {deleteTarget?.type === 'readiness'
                  ? 'readiness checklist'
                  : deleteTarget?.type === 'gtm'
                    ? 'GTM plan'
                    : deleteTarget?.type === 'launch'
                      ? 'launch checklist'
                      : 'commercialisation record'}
                ? This action cannot be undone.
              </p>
              <div className="flex gap-3">
                <button
                  onClick={handleDelete}
                  className="flex-1 rounded-lg bg-red-600 px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-red-700"
                >
                  Delete
                </button>
                <button
                  onClick={() => {
                    setShowDeleteModal(false);
                    setDeleteTarget(null);
                  }}
                  className="flex-1 rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 transition-colors hover:bg-gray-50"
                >
                  Cancel
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default AdminPanel;
